import React, { memo, useEffect, useMemo, useState } from "react";
import { Tooltip } from "react-tooltip";
import _debounce from "lodash/debounce";
import {
  RangeDropdown,
  ExcelBtn,
  SearchBar,
  Pagination,
  NoRecordFound,
} from "../../component/commonComponent";
import doubleArrow from "../../assets/admin Images/2arrows.svg";
import { PageSize } from "../../utils/constant";
import { exportToCSV, toastMessage } from "../../utils/helper";
import userAuth from "../../utils/api/userAuth";
import moment from "moment";
import PlatformSurveyFilterBtn from "../../component/commonComponent/PlatformSurveyFilterBtn";
import TIMER_ICON from "../../assets/images/clock-filter.svg";
import axios from "axios";
import { getToken } from "../../utils/helper";

const mapCsatResponseToSurveyRow = (item) => {
  const overall = item?.overallInterviewExperience || {};
  const evaluation = item?.evaluationAndFeedbackQuality || {};
  const scheduling = item?.schedulingAndCoordination || {};

  return {
    interviewId: item?.interviewCode || "",
    employerName: item?.employerName || "",
    responderEmail: item?.responderEmail || item?.email || "",
    product: item?.productName || "",
    overallInterviewExperience: {
      score: overall?.score ?? 0,
      comment: overall?.comment || "",
    },
    evaluationAndFeedbackQuality: {
      score: evaluation?.score ?? 0,
      comment: evaluation?.comment || "",
    },
    schedulingAndCoordination: {
      score: scheduling?.score ?? 0,
      comment: scheduling?.comment || "",
    },
    suggestions: item?.additionalComments || "",
    submittedBy: item?.responderUserName || "",
    submittedOn: item?.submittedAt || "",
  };
};

// Every column shares the same flex basis so widths stay uniform.
const cellStyle = {
  flex: "1 1 0",
  minWidth: 0,
};

const columns = [
  {
    name: "Employer Name",
    sortBy: "employerName",
    getValue: (item) => item.employerName,
  },
  {
    name: "Interview ID",
    sortBy: "interviewCode",
    getValue: (item) => item.interviewId,
  },
  {
    name: "Product",
    sortBy: "",
    getValue: (item) => item.product,
  },
  {
    name: "Overall Interview Experience",
    sortBy: "",
    getValue: (item) => `${item.overallInterviewExperience?.score ?? 0}/5`,
    getTooltip: (item) =>
      item.overallInterviewExperience?.comment ||
      `${item.overallInterviewExperience?.score ?? 0}/5`,
  },
  {
    name: "Evaluation & Feedback Quality",
    sortBy: "",
    getValue: (item) => `${item.evaluationAndFeedbackQuality?.score ?? 0}/5`,
    getTooltip: (item) =>
      item.evaluationAndFeedbackQuality?.comment ||
      `${item.evaluationAndFeedbackQuality?.score ?? 0}/5`,
  },
  {
    name: "Scheduling & Coordination",
    sortBy: "",
    getValue: (item) => `${item.schedulingAndCoordination?.score ?? 0}/5`,
    getTooltip: (item) =>
      item.schedulingAndCoordination?.comment ||
      `${item.schedulingAndCoordination?.score ?? 0}/5`,
  },
  {
    name: "Suggestions / Remarks",
    sortBy: "",
    getValue: (item) => item.suggestions,
  },
  {
    name: "Submitted by",
    sortBy: "",
    getValue: (item) => item.submittedBy,
  },
  {
    name: "Submitted on",
    sortBy: "",
    getValue: (item) =>
      item.submittedOn
        ? `${moment(item.submittedOn).format("Do MMM, YYYY")}, ${moment(
            item.submittedOn,
          ).format("h:mm A")}`
        : "",
  },
];

const defaultFilters = {
  from: "",
  to: "",
  user: "",
  employer: "",
  rating: "",
  category: "",
  productId: "",
  surveyType: "all",
};

const tooltipStyle = {
  maxWidth: "260px",
  whiteSpace: "normal",
  wordWrap: "break-word",
  userSelect: "text",
  zIndex: 999999,
  padding: "4px",
  fontSize: "12px",
};

const InterviewSurveyTable = memo(() => {
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesLimit, setEntriesLimit] = useState(PageSize);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [isAsc, setIsAsc] = useState(true);
  const [surveyData, setSurveyData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFiltersState] = useState({ ...defaultFilters });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const handleEntriesLimit = (limit) => {
    setEntriesLimit(limit);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleChangeSort = (key) => {
    if (sortBy === key) {
      setIsAsc(!isAsc);
    } else {
      setSortBy(key);
      setIsAsc(true);
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const applyFilters = (nextFilters) => {
    setCurrentPage(1);
    const merged = { ...defaultFilters, ...nextFilters };

    if (!merged.user && merged.employer) {
      merged.user = merged.employer;
    }

    setFiltersState(merged);
  };

  const filtersForControl = useMemo(() => {
    return {
      from: filters.from ? moment(filters.from).format("YYYY-MM-DD") : "",
      to: filters.to ? moment(filters.to).format("YYYY-MM-DD") : "",
      employer: filters.employer || "",
      employerUserId: filters.user || "",
      rating:
        filters.rating === null || filters.rating === undefined
          ? ""
          : String(filters.rating),
      category: filters.category || "",
      productId: filters.productId || "",
      surveyType: filters.surveyType || "all",
    };
  }, [filters]);

  useEffect(() => {
    const fetchPlatformSurvey = async () => {
      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("pageSize", String(entriesLimit));

        if (filters.from) {
          params.set("from", filters.from);
        }

        if (filters.to) {
          params.set("to", filters.to);
        }

        if (filters.user) {
          params.set("user", filters.user);
        }

        const normalizedRating =
          filters.rating === null ||
          filters.rating === undefined ||
          String(filters.rating).trim() === ""
            ? ""
            : String(filters.rating).trim();

        if (normalizedRating) {
          params.set("rating", normalizedRating);
        }

        if (filters.category && String(filters.category).trim()) {
          params.set("category", String(filters.category).trim());
        }

        if (filters.productId && String(filters.productId).trim()) {
          params.set("productId", String(filters.productId).trim());
        }

        if (filters.surveyType && String(filters.surveyType).trim() !== "all") {
          params.set("surveyType", String(filters.surveyType).trim());
        }

        if (search.trim()) {
          params.set("search", search.trim());
        }

        if (sortBy) {
          const direction = isAsc ? "asc" : "desc";
          params.set("sort", `${sortBy} ${direction}`);
        }

        const queryString = params
          .toString()
          .replace(/%3A/gi, ":")
          .replace(/%2C/gi, ",");
        const endpoint = queryString
          ? `/admin/users/csat/responses?${queryString}`
          : "/admin/users/csat/responses";

        const response = await userAuth.get(endpoint);
        // const response = await axios.get(
        //   `http://172.16.2.9:4001/api/v1/admin/users/csat/responses?${queryString}`,
        //   {
        //     headers: {
        //       Authorization: `Bearer ${getToken()}`,
        //     },
        //   },
        // );
        const apiData = response?.data?.result?.data || [];
        const mappedData = apiData.map(mapCsatResponseToSurveyRow);
        setSurveyData(mappedData);
        // Extract total count from API response - check multiple possible locations
        const total =
          response?.data?.result?.total ||
          response?.data?.result?.count ||
          response?.data?.total ||
          mappedData.length;
        setTotalCount(total);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "Failed to load interview survey data.";
        toastMessage("error", message);
        setSurveyData([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatformSurvey();
  }, [
    currentPage,
    entriesLimit,
    filters.from,
    filters.to,
    filters.user,
    filters.rating,
    filters.category,
    filters.productId,
    filters.surveyType,
    search,
    sortBy,
    isAsc,
    refreshTrigger,
  ]);

  // API handles filtering, sorting, and pagination server-side
  // So we use surveyData directly without client-side processing

  const handleRefresh = _debounce(() => {
    setSortBy("");
    setSearch("");
    setCurrentPage(1);
    applyFilters(defaultFilters);
    setRefreshTrigger((prev) => prev + 1);
  }, 300);

  const surveyDataForCsv = useMemo(() => {
    return surveyData.map((item) => ({
      "Employer Name": item.employerName,
      "Interview ID": item.interviewId,
      Product: item.product,
      "Overall Interview Experience":
        item.overallInterviewExperience?.score ?? "",
      "Evaluation & Feedback Quality":
        item.evaluationAndFeedbackQuality?.score ?? "",
      "Scheduling & Coordination": item.schedulingAndCoordination?.score ?? "",
      "Suggestions / Remarks": item.suggestions,
      "Submitted by": item.submittedBy,
      "Submitted on - Date": moment(item.submittedOn).format("Do MMM, YYYY"),
      "Submitted on - Time": moment(item.submittedOn).format("h:mm A"),
    }));
  }, [surveyData]);

  return (
    <>
      <div className="page-container eep">
        <div className="mpl-title-container d-flex justify-content-between align-items-center rounded-3">
          <div className="text-theme-blue f-sem-bol f-20">
            Interview Survey {`(${totalCount})`}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between">
          <RangeDropdown handleEntriesLimit={handleEntriesLimit} />
          <div className="d-flex align-items-center">
            <SearchBar
              onChange={handleSearch}
              value={search}
              containerClass={"search-bar-container-type1"}
              inputClass={"search-input-type1"}
            />
            <img
              src={TIMER_ICON}
              alt="timer"
              className="refresh-btn"
              onClick={handleRefresh}
            />
            <PlatformSurveyFilterBtn
              setFilters={applyFilters}
              activeFilters={filtersForControl}
              showNpsCategory={false}
              showDsatCsatDropdown
            />
            {surveyData.length > 0 ? (
              <ExcelBtn
                onClick={_debounce(
                  () => exportToCSV(surveyDataForCsv, "Interview Survey List"),
                  300,
                )}
              />
            ) : (
              <ExcelBtn excelBtnClassName="excel-grey-btn cursor-na" />
            )}
          </div>
        </div>
        <div className="sp-list">
          <div className="header d-flex f-12 f-sem-bol rounded-3">
            {columns.map((data, index) => (
              <div key={index} className="header-content" style={cellStyle}>
                <span>{data.name}</span>
                {data.sortBy && (
                  <img
                    src={doubleArrow}
                    alt="double"
                    className="img-fluid"
                    onClick={() => handleChangeSort(data.sortBy)}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="row-container">
            {isLoading ? (
              <div className="text-center py-3">Loading...</div>
            ) : surveyData.length > 0 ? (
              surveyData.map((item, index) => (
                <div
                  className="support-row d-flex f-12 f-sem-bol rounded-3"
                  key={index}
                >
                  {columns.map((col, colIndex) => {
                    const value = col.getValue(item);
                    const tooltipContent = col.getTooltip
                      ? col.getTooltip(item)
                      : value;
                    const tooltipId = `interview-survey-tooltip-${index}-${colIndex}`;
                    return (
                      <div
                        key={colIndex}
                        className="row-content"
                        style={cellStyle}
                      >
                        <span
                          className={`t-clip w-100 text-center ${
                            col.className || ""
                          }`}
                          data-tooltip-id={
                            tooltipContent ? tooltipId : undefined
                          }
                          data-tooltip-content={tooltipContent || undefined}
                          data-tooltip-place="top"
                        >
                          {value}
                        </span>
                        {tooltipContent && (
                          <Tooltip
                            id={tooltipId}
                            className="tooltip-custom"
                            positionStrategy="fixed"
                            style={tooltipStyle}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))
            ) : (
              <NoRecordFound />
            )}
          </div>
        </div>
        {surveyData.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={entriesLimit}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </>
  );
});

export default InterviewSurveyTable;
